// EP-135 Tier 3 — Customer-facing meeting detail (replaces the Tier 1a
// "coming soon" placeholder at /meetings/:id).
//
// Renders the meeting metadata + per-resolution ballot cards. Each
// resolution exposes:
//   - the title + body
//   - the per-resolution tally (aggregate, never per-voter — §4a)
//   - the customer's own_vote receipt when secret:false + already voted
//   - a Cast ballot button when status='in_progress' AND the customer
//     is entitled to cast (own_attendee OR proxy holder; §12 S-4).
//
// Per D-2, secret resolutions have their `own_vote` redacted at the
// serializer; the UI never sees it. The Cast ballot modal lets the
// customer pick which attendee they're casting for (own_attendee + any
// proxy_for rows) and the choice.
//
// EP-135 Tier 4 — Adds the customer-side read-only minutes surface:
// when `meeting.status === 'certified'`, the page renders a "View signed
// minutes" toggle that lazily fetches the DocumentBuilder payload from
// GET /api/customer/meetings/:id/minutes and renders the sectioned
// document inline (header / attendance / resolutions / certification).
// The controller enforces the status='certified' precondition
// server-side, so any fetch that finds a not-yet-certified meeting
// surfaces a `minutes_not_yet_certified` 422 — handled here as a
// user-visible "not yet certified" message.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useCastVote, useMeeting, useMeetingMinutes } from '@/hooks/useMeetings'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type {
  CustomerMinutesAttendeeRow,
  CustomerMinutesResolutionRow,
  CustomerMinutesSection,
  CustomerResolution,
  MeetingStatus,
  VoteChoice,
} from '@/types/meetings'

const STATUS_BADGE: Record<MeetingStatus, string> = {
  draft: 'status-badge-ghost',
  noticed: 'status-badge-info',
  in_progress: 'status-badge-warning',
  voting_closed: 'status-badge-info',
  certified: 'status-badge-success',
}

function weightRatio(weight: string): string {
  const n = Number(weight)
  if (Number.isNaN(n)) return weight
  // Display as percent with no trailing zero (0.5 → 50%, 0.333 → 33.3%)
  return `${(n * 100).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  })}%`
}

export default function MeetingDetailPage() {
  const { t } = useTranslation('meetings')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const meetingId = id ? Number(id) : 0
  const { formatDateTime } = useDateFormatter()
  const { data: meeting, isLoading, error } = useMeeting(meetingId)

  const [openResolutionId, setOpenResolutionId] = useState<number | null>(null)
  const [showMinutes, setShowMinutes] = useState(false)

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !meeting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center">
        <p className="text-base-content/60">
          {tc('errors.something_went_wrong')}
        </p>
        <Link to="/meetings" className="btn btn-ghost btn-sm mt-4">
          <ArrowLeftIcon className="w-4 h-4" />
          {tc('actions.back')}
        </Link>
      </div>
    )
  }

  const isCertified = meeting.status === 'certified'

  return (
    <div className="space-y-0 pb-6">
      <div className="bg-primary px-4 pt-12 pb-5 flex items-center gap-3">
        <Link
          to="/meetings"
          className="text-primary-content/80 hover:text-primary-content"
          aria-label={tc('actions.back')}
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        <h1 className="text-primary-content font-bold text-base truncate flex-1 min-w-0">
          {meeting.title}
        </h1>
      </div>

      <div className="px-4 pt-5 space-y-5">
        {/* Metadata */}
        <div className="flat-card p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className={STATUS_BADGE[meeting.status]}>
              {t(`status.${meeting.status}`)}
            </span>
            {meeting.call_number > 1 && (
              <span className="text-[11px] text-base-content/60">
                {t('call_n', { n: meeting.call_number })}
              </span>
            )}
          </div>
          <div className="text-xs text-base-content/60 space-y-1">
            <p>{formatDateTime(meeting.meeting_at)}</p>
            {meeting.venue && <p>{meeting.venue}</p>}
            {meeting.meeting_url && (
              <a
                href={meeting.meeting_url}
                className="link link-primary break-all"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('join_meeting')}
              </a>
            )}
          </div>
          {meeting.own_attendee && (
            <div className="text-[11px] text-base-content/50 pt-2 border-t border-dark-50">
              {t('your_weight', {
                weight: weightRatio(meeting.own_attendee.weight_snapshot),
              })}
            </div>
          )}
        </div>

        {/* Resolutions */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-base-content/80">
            {t('resolutions_title')}
          </h2>
          {meeting.resolutions.length === 0 ? (
            <div className="flat-card p-6 text-center">
              <p className="text-sm text-base-content/60">
                {t('resolutions_empty')}
              </p>
            </div>
          ) : (
            meeting.resolutions.map((r) => (
              <ResolutionCard
                key={r.id}
                resolution={r}
                meetingStatus={meeting.status}
                ownAttendee={meeting.own_attendee}
                proxyFor={meeting.proxy_for}
                isOpen={openResolutionId === r.id}
                onOpenChange={(next) =>
                  setOpenResolutionId(next ? r.id : null)
                }
              />
            ))
          )}
        </div>

        {/* EP-135 Tier 4 — Minutes: visible only when the meeting is
            certified (server-enforced). The lazy fetch is gated on
            `showMinutes` so we never hit the API on page load for a
            not-yet-certified meeting. */}
        {isCertified && (
          <div
            className="flat-card p-4 space-y-3"
            data-testid="minutes-card"
          >
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-success" />
              <h2 className="text-sm font-semibold text-base-content">
                {t('minutes.title')}
              </h2>
            </div>

            {!showMinutes && (
              <button
                type="button"
                onClick={() => setShowMinutes(true)}
                className="btn btn-primary btn-sm w-full"
                data-testid="btn-view-minutes"
              >
                <DocumentTextIcon className="w-4 h-4 mr-1" />
                {t('minutes.view_minutes')}
              </button>
            )}

            {showMinutes && (
              <MinutesDocument meetingId={meetingId} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface ResolutionCardProps {
  resolution: CustomerResolution
  meetingStatus: MeetingStatus
  ownAttendee: { id: number; presence: string; weight_snapshot: string; checked_in_at: string | null } | null
  proxyFor: Array<{
    attendee_id: number
    owner_customer_id: number
    owner_customer_name: string
    unit_label: string | null
  }>
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

function ResolutionCard({
  resolution,
  meetingStatus,
  ownAttendee,
  proxyFor,
  isOpen,
  onOpenChange,
}: ResolutionCardProps) {
  const { t } = useTranslation('meetings')
  // Hooks must be unconditional — this was previously invoked inline
  // inside the `{isOpen && canCast && …}` JSX branch (rules-of-hooks).
  const meetingId = useMeetingIdFromParams()

  const canCast =
    meetingStatus === 'in_progress' &&
    (ownAttendee !== null || proxyFor.length > 0)

  const alreadyVoted = resolution.own_vote !== null
  const isSecret = resolution.secret

  return (
    <div className="flat-card p-4 space-y-3" data-testid={`resolution-${resolution.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-base-content/50">
            {t('resolution_seq', { seq: resolution.seq })}
          </p>
          <p className="font-semibold text-sm text-base-content">
            {resolution.title}
          </p>
        </div>
        {isSecret && (
          <span
            className="status-badge-ghost inline-flex items-center gap-1"
            aria-label={t('secret_ballot')}
          >
            <LockClosedIcon className="w-3 h-3" />
            {t('secret_ballot')}
          </span>
        )}
      </div>

      {resolution.body && (
        <p className="text-xs text-base-content/70 whitespace-pre-wrap">
          {resolution.body}
        </p>
      )}

      {/* Tally (always visible — the aggregate is public) */}
      {resolution.tally && (
        <div className="grid grid-cols-3 gap-2 text-center pt-2">
          <TallyCell label={t('choices.for')} value={resolution.tally.for_weight} />
          <TallyCell label={t('choices.against')} value={resolution.tally.against_weight} />
          <TallyCell label={t('choices.abstain')} value={resolution.tally.abstain_weight} />
        </div>
      )}

      {/* Own vote receipt — never shown for secret resolutions */}
      {resolution.own_vote && !isSecret && (
        <div className="flex items-center gap-2 text-xs text-success pt-2 border-t border-dark-50">
          <CheckCircleIcon className="w-4 h-4" />
          <span>
            {t('you_voted', {
              choice: t(`choices.${resolution.own_vote.choice}`),
            })}
          </span>
        </div>
      )}

      {/* Cast / Change ballot action */}
      {canCast && (
        <button
          type="button"
          onClick={() => onOpenChange(!isOpen)}
          className="btn btn-primary btn-sm w-full"
          data-testid={`btn-cast-${resolution.id}`}
        >
          {alreadyVoted ? t('change_ballot') : t('cast_ballot')}
        </button>
      )}

      {isOpen && canCast && (
        <BallotForm
          meetingId={meetingId}
          resolutionId={resolution.id}
          ownAttendee={ownAttendee}
          proxyFor={proxyFor}
          currentChoice={resolution.own_vote?.choice ?? null}
          onClose={() => onOpenChange(false)}
        />
      )}
    </div>
  )
}

function useMeetingIdFromParams(): number {
  const { id } = useParams<{ id: string }>()
  return id ? Number(id) : 0
}

function TallyCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-base-200/40 rounded-md py-2">
      <p className="text-[10px] text-base-content/60">{label}</p>
      <p className="font-semibold text-sm text-base-content">{value}</p>
    </div>
  )
}

interface BallotFormProps {
  meetingId: number
  resolutionId: number
  ownAttendee: ResolutionCardProps['ownAttendee']
  proxyFor: ResolutionCardProps['proxyFor']
  currentChoice: VoteChoice | null
  onClose: () => void
}

function BallotForm({
  meetingId,
  resolutionId,
  ownAttendee,
  proxyFor,
  currentChoice,
  onClose,
}: BallotFormProps) {
  const { t } = useTranslation('meetings')
  const { t: tc } = useTranslation('common')

  // Default to the customer's own attendee row; the proxy picker only
  // appears when there are proxy rows to choose from.
  const initialAttendeeId = ownAttendee?.id ?? proxyFor[0]?.attendee_id ?? 0
  const [attendeeId, setAttendeeId] = useState<number>(initialAttendeeId)
  const [choice, setChoice] = useState<VoteChoice>(currentChoice ?? 'for')

  const cast = useCastVote(meetingId)

  const handleSubmit = async () => {
    try {
      await cast.mutateAsync({ resolutionId, attendeeId, choice })
      onClose()
    } catch {
      window.alert(tc('errors.something_went_wrong'))
    }
  }

  const attendeeOptions: Array<{ value: number; label: string }> = []
  if (ownAttendee) {
    attendeeOptions.push({
      value: ownAttendee.id,
      label: t('attendee_self'),
    })
  }
  for (const p of proxyFor) {
    attendeeOptions.push({
      value: p.attendee_id,
      label: t('attendee_proxy', { name: p.owner_customer_name }),
    })
  }

  return (
    <div
      className="pt-3 border-t border-dark-50 space-y-3"
      data-testid={`ballot-form-${resolutionId}`}
    >
      {attendeeOptions.length > 1 && (
        <div>
          <label className="text-xs font-medium text-base-content/80 block mb-1">
            {t('cast_for')}
          </label>
          <select
            className="select select-bordered select-sm w-full"
            value={attendeeId}
            onChange={(e) => setAttendeeId(Number(e.target.value))}
            data-testid={`select-attendee-${resolutionId}`}
          >
            {attendeeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="text-xs font-medium text-base-content/80 block mb-1">
          {t('your_choice')}
        </label>
        <div className="grid grid-cols-3 gap-2" data-testid={`choice-group-${resolutionId}`}>
          {(['for', 'against', 'abstain'] as VoteChoice[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setChoice(c)}
              className={`btn btn-sm ${choice === c ? 'btn-primary' : 'btn-outline'}`}
              data-testid={`choice-${c}-${resolutionId}`}
            >
              {t(`choices.${c}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-ghost btn-sm"
        >
          {tc('actions.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={cast.isPending || !attendeeId}
          className="btn btn-primary btn-sm"
          data-testid={`btn-submit-ballot-${resolutionId}`}
        >
          {cast.isPending ? tc('actions.saving') : t('submit_ballot')}
        </button>
      </div>
    </div>
  )
}

// EP-135 Tier 4 — Read-only minutes document viewer (customer side).
//
// Renders the same sectioned document the operator sees, minus the
// signing controls + audit trail. The controller enforces status:
// 'certified', so a non-certified meeting never reaches this component
// (the parent only renders the "view minutes" trigger when
// `meeting.status === 'certified'`).
function MinutesDocument({ meetingId }: { meetingId: number }) {
  const { t } = useTranslation('meetings')
  const { data: doc, isLoading, error } = useMeetingMinutes(meetingId)

  if (isLoading) {
    return (
      <div className="py-6 text-center text-base-content/60 text-xs">
        {t('resolutions_empty')}
      </div>
    )
  }

  if (error || !doc) {
    return (
      <div
        className="py-4 text-center text-error text-xs"
        data-testid="minutes-not-yet-certified"
      >
        {t('minutes.not_yet_certified_title')}
      </div>
    )
  }

  return (
    <div className="space-y-4 pt-2 border-t border-dark-50">
      {doc.sections.map((section, idx) => (
        <MinutesSectionView key={idx} section={section} />
      ))}
    </div>
  )
}

function MinutesSectionView({ section }: { section: CustomerMinutesSection }) {
  const { t } = useTranslation('meetings')

  switch (section.kind) {
    case 'header':
      return (
        <section data-testid="liff-minutes-section-header">
          <h3 className="text-xs font-semibold text-base-content/80 mb-1">
            {t('minutes.sections.header')}
          </h3>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <dt className="text-base-content/60">{t('minutes.labels.meeting_type')}</dt>
              <dd>{section.meeting_type_label}</dd>
            </div>
            <div>
              <dt className="text-base-content/60">{t('minutes.labels.call_number')}</dt>
              <dd>{section.call_number}</dd>
            </div>
            <div>
              <dt className="text-base-content/60">{t('minutes.labels.notice_lead_days')}</dt>
              <dd>{section.notice_lead_days}</dd>
            </div>
            {section.agenda && (
              <div className="col-span-2">
                <dt className="text-base-content/60">{t('minutes.labels.agenda')}</dt>
                <dd className="whitespace-pre-wrap">{section.agenda}</dd>
              </div>
            )}
          </dl>
        </section>
      )

    case 'attendance':
      return (
        <section data-testid="liff-minutes-section-attendance">
          <h3 className="text-xs font-semibold text-base-content/80 mb-1">
            {t('minutes.sections.attendance')}
          </h3>
          <div className="overflow-x-auto">
            <table className="table table-xs">
              <thead>
                <tr>
                  <th></th>
                  <th>Unit</th>
                  <th>{t('minutes.labels.presence')}</th>
                  <th className="text-right">{t('minutes.labels.weight')}</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <AttendanceRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )

    case 'resolutions':
      return (
        <section data-testid="liff-minutes-section-resolutions">
          <h3 className="text-xs font-semibold text-base-content/80 mb-1">
            {t('minutes.sections.resolutions')}
          </h3>
          {section.rows.length === 0 ? (
            <p className="text-xs text-base-content/60">
              {t('resolutions_empty')}
            </p>
          ) : (
            <ol className="space-y-3 list-decimal pl-5">
              {section.rows.map((row) => (
                <ResolutionRow key={row.id} row={row} />
              ))}
            </ol>
          )}
        </section>
      )

    case 'certification': {
      const cert = section
      return (
        <section data-testid="liff-minutes-section-certification">
          <h3 className="text-xs font-semibold text-base-content/80 mb-1">
            {t('minutes.sections.certification')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <CertificationSignerCell
              label={t('minutes.labels.chair')}
              signer={cert.chair}
              certifiedAt={cert.certified_at}
            />
            <CertificationSignerCell
              label={t('minutes.labels.secretary')}
              signer={cert.secretary}
              certifiedAt={cert.certified_at}
            />
          </div>
        </section>
      )
    }
  }
}

function AttendanceRow({ row }: { row: CustomerMinutesAttendeeRow }) {
  return (
    <tr>
      <td>
        <span className="text-base-content">{row.customer_name}</span>
        {row.proxy_holder_name && (
          <span className="block text-[10px] text-base-content/60">
            ({row.proxy_holder_name})
          </span>
        )}
      </td>
      <td className="text-base-content/70">{row.unit_label ?? '—'}</td>
      <td>
        <span className="status-badge-ghost">{row.presence}</span>
      </td>
      <td className="text-right tabular-nums">{row.weight_snapshot}</td>
    </tr>
  )
}

function ResolutionRow({ row }: { row: CustomerMinutesResolutionRow }) {
  const { t } = useTranslation('meetings')

  return (
    <li data-testid={`liff-minutes-resolution-${row.id}`}>
      <div className="flex items-center gap-2">
        <span className="font-medium">{row.title}</span>
        {row.secret && (
          <span
            className="status-badge-ghost inline-flex items-center gap-1"
            aria-label={t('minutes.labels.secret')}
          >
            <LockClosedIcon className="w-3 h-3" />
            {t('minutes.labels.secret')}
          </span>
        )}
        <span className="status-badge-ghost">{row.status}</span>
      </div>
      {row.body && <p className="text-xs text-base-content/70 mt-1">{row.body}</p>}
      <div className="text-[11px] text-base-content/60 mt-1">
        {t('minutes.labels.outcome')}: {row.tally.decision ?? '—'}
        {' · '}
        {t('minutes.labels.total_cast')}: {row.tally.total_cast}
      </div>
    </li>
  )
}

function CertificationSignerCell({
  label,
  signer,
  certifiedAt,
}: {
  label: string
  signer: { id: number; name: string; signed_at: string | null } | null
  certifiedAt: string | null
}) {
  if (!signer) {
    return (
      <div className="border border-base-300 rounded-md p-2">
        <dt className="text-base-content/60">{label}</dt>
        <dd className="text-base-content/40 mt-1">—</dd>
      </div>
    )
  }
  return (
    <div className="border border-base-300 rounded-md p-2">
      <dt className="text-base-content/60">{label}</dt>
      <dd className="mt-1">{signer.name}</dd>
      {certifiedAt && (
        <dd className="text-[10px] text-base-content/60">
          {new Date(certifiedAt).toLocaleString()}
        </dd>
      )}
    </div>
  )
}
